import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchProductPriority,
  patchProductPriority,
  type ProductPriorityOrder,
  addUser,
  assignUserRole,
  deactivateUser,
  fetchOrganization,
  fetchPermissions,
  fetchRoleAliases,
  fetchUsers,
  patchOrganization,
  patchPermissions,
  patchRoleAliases,
  type AddUserInput,
  type OrganizationPatch,
  type PermissionMatrix,
  type RoleAliases,
  type SettingsOrganization,
  type SettingsUser,
} from './settings-api';

/**
 * Settings query + mutation hooks (SCRUM-460). One key per resource; mutations
 * invalidate their query so the UI reflects the persisted state.
 */
const ORGANIZATION_KEY = ['settings-organization'] as const;
const USERS_KEY = ['settings-users'] as const;
const PERMISSIONS_KEY = ['settings-permissions'] as const;
const PRODUCT_PRIORITY_KEY = ['settings-product-priority'] as const;
const ROLE_ALIASES_KEY = ['settings-role-aliases'] as const;

export function useOrganization(): {
  organization: SettingsOrganization | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<SettingsOrganization>({
    queryKey: ORGANIZATION_KEY,
    queryFn: fetchOrganization,
    retry: false,
  });
  return { organization: data, isLoading, isError };
}

export function useUpdateOrganization(): {
  save: (patch: OrganizationPatch) => Promise<void>;
  isSaving: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (patch: OrganizationPatch) => patchOrganization(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEY }),
  });
  return { save: mutation.mutateAsync, isSaving: mutation.isPending };
}

/** The tenant's users — feeds the Organization primary-admin picker (SCRUM-460). */
export function useSettingsUsers(): {
  users: SettingsUser[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<SettingsUser[]>({
    queryKey: USERS_KEY,
    queryFn: fetchUsers,
    retry: false,
  });
  return { users: data ?? [], isLoading, isError };
}

// ── Roles & permissions (SCRUM-461) ───────────────────────────────────────────

export function usePermissions(): {
  matrix: PermissionMatrix | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery<PermissionMatrix>({
    queryKey: PERMISSIONS_KEY,
    queryFn: fetchPermissions,
    retry: false,
  });
  return { matrix: data, isLoading, isError };
}

export function useUpdatePermissions(): {
  save: (matrix: PermissionMatrix) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (matrix: PermissionMatrix) => patchPermissions(matrix),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PERMISSIONS_KEY }),
  });
  return { save: mutation.mutateAsync };
}

// ── Product priority (SCRUM-46) ───────────────────────────────────────────────
// Owned HERE (settings) as the single source of the priority query; the
// campaigns drawer/Refer popover IMPORT useProductPriority so a Settings save
// invalidates the SAME cache key they read (no stale-order bug — RT-A).

/**
 * The global product-priority order. Consumed both by the Settings section
 * (read + save) and by the ranked-product surfaces (read-only) — one shared
 * query key means a save re-renders both.
 */
export function useProductPriority(): {
  order: ProductPriorityOrder | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery<ProductPriorityOrder>({
    queryKey: PRODUCT_PRIORITY_KEY,
    queryFn: fetchProductPriority,
    retry: false,
  });
  return { order: data, isLoading };
}

export function useUpdateProductPriority(): {
  save: (order: ProductPriorityOrder) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (order: ProductPriorityOrder) => patchProductPriority(order),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRODUCT_PRIORITY_KEY }),
  });
  return { save: mutation.mutateAsync };
}

// ── Role name aliases (SCRUM-465) ─────────────────────────────────────────────
// Owned HERE as the single source of the alias query; useDisplayRole IMPORTS
// useRoleAliases so a Settings save invalidates the SAME cache key every
// role-name render reads (no stale-alias bug — mirrors product priority RT-A).

/**
 * The tenant's per-role display-name aliases. Consumed by the Settings editor
 * (read + save) and by useDisplayRole (read-only) everywhere a role name
 * renders — one shared query key means a save re-renders every consumer.
 */
export function useRoleAliases(): {
  aliases: RoleAliases | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery<RoleAliases>({
    queryKey: ROLE_ALIASES_KEY,
    queryFn: fetchRoleAliases,
    retry: false,
  });
  return { aliases: data, isLoading };
}

export function useUpdateRoleAliases(): {
  save: (aliases: RoleAliases) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (aliases: RoleAliases) => patchRoleAliases(aliases),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROLE_ALIASES_KEY }),
  });
  return { save: mutation.mutateAsync };
}

/** Add / assign-role / deactivate — each invalidates the users list on success. */
export function useUserMutations(): {
  add: (input: AddUserInput) => Promise<string>;
  assignRole: (userId: string, role: string) => Promise<void>;
  deactivate: (userId: string) => Promise<void>;
  isAdding: boolean;
} {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: USERS_KEY });

  const addMutation = useMutation({ mutationFn: addUser, onSuccess: invalidate });
  const assignMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      assignUserRole(userId, role),
    onSuccess: invalidate,
  });
  const deactivateMutation = useMutation({ mutationFn: deactivateUser, onSuccess: invalidate });

  return {
    add: addMutation.mutateAsync,
    assignRole: (userId, role) => assignMutation.mutateAsync({ userId, role }),
    deactivate: deactivateMutation.mutateAsync,
    isAdding: addMutation.isPending,
  };
}
